import {
  NotificationPreference,
  NotificationPreferencesProps,
  NotificationPreferencesSection,
  useAuthentication,
} from '@ecosystem/newron-client-lib';
import { inputs } from '@ecosystem/newron-design-system';
import React, { useEffect, useMemo, useState } from 'react';
import { useFetchEntitledMarkets } from '../hooks/useFetchEntitledMarkets.hook';
import { Proposition } from '../utils/constants';

type NotificationUpdatesProps = NotificationPreferencesProps & {
  type: 'market-updates' | 'distribution-updates';
  title: string;
};

const NotificationUpdatePreferences: React.FC<NotificationPreferencesProps> = ({
  type,
  title,
  ...preferencesProps
}: NotificationUpdatesProps) => {
  const { entitledMarkets = [] } = useFetchEntitledMarkets(
    type === 'distribution-updates' ? Proposition.DistributionPanel : Proposition.Market
  );
  const [filteredPreferences, setFilteredPreferences] = useState<NotificationPreference>({
    type: '',
    settings: { email: false, push: false },
    context: {} as any,
  });
  const selectedMarkets = useMemo(
    () => filteredPreferences?.context?.markets?.map(({ countryId, itemGroupId }) => `${itemGroupId}:${countryId}`),
    [filteredPreferences]
  );
  const { isAuthenticated } = useAuthentication();

  const options = useMemo(
    () =>
      entitledMarkets.map(market => ({
        value: `${market.productCode}:${market.countryCode}`,
        label: market.label,
      })),
    [entitledMarkets]
  );

  useEffect(() => {
    if (entitledMarkets?.length) {
      setFilteredPreferences(preferencesProps.preferences);
    }
  }, [entitledMarkets, preferencesProps.preferences]);

  const onSelectHandler = (values: string[]) => {
    const updatedPreferences = {
      type,
      settings: {
        email: filteredPreferences?.settings?.email || false,
        push: filteredPreferences?.settings?.push || false,
      },
      context: {
        markets: values.map(value => ({
          countryId: value.split(':')[1],
          itemGroupId: value.split(':')[0],
        })),
      },
    };
    preferencesProps.onChange(updatedPreferences, isValid(updatedPreferences), hasDataLoadingError());
    setFilteredPreferences(updatedPreferences);
  };

  const hasDataLoadingError = (): boolean => {
    if (options.length === 0) {
      return true;
    }

    return false;
  };

  const isValid = (preferences: NotificationPreference): boolean => {
    return (
      (Object.values(preferences?.settings).some(setting => setting) && !!preferences?.context?.markets?.length) ||
      Object.values(preferences?.settings).every(setting => !setting)
    );
  };

  if (!isAuthenticated) {
    return null;
  }

  const noSettingsSelected = Object.values(preferencesProps?.preferences?.settings).every(setting => !setting);

  return (
    <NotificationPreferencesSection
      title={title}
      type={type}
      email
      push
      preferences={filteredPreferences}
      onChange={prefs => preferencesProps.onChange(prefs, isValid(prefs), hasDataLoadingError())}
      checkBoxTestId={type}
    >
      <inputs.Select
        onChange={onSelectHandler}
        options={options}
        placeholder={'Choose markets'}
        multi
        header="Which areas should we notify you about?"
        value={selectedMarkets}
        name="market-select"
        customErrorOptionMessage="Data has not been loaded"
        disabled={noSettingsSelected}
        hasError={preferencesProps.showError}
        selectionType="checkbox"
        clearButtonPosition="above"
      />
      {preferencesProps.showError && (
        <div className="text-sm mt-2 mb-6 font-normal text-ui-red">
          Please select at least one market to save these settings.
        </div>
      )}
    </NotificationPreferencesSection>
  );
};

export const DistributionPreferences = props => {
  return <NotificationUpdatePreferences {...props} type="distribution-updates" title="gfknewron Supply Chain" />;
};

export const MarketPreferences = props => {
  return <NotificationUpdatePreferences {...props} type="market-updates" title="gfknewron Markets" />;
};
